// Thin fetch wrapper — cookies sent automatically (credentials: 'include')
import * as Sentry from '@sentry/react'
import { env } from '../env'
import { useAuthStore } from '../store/authStore'

const BASE = env.apiBaseUrl

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch (err) {
    // Network / fetch failure (DNS, offline, CORS, …) — not visible in backend logs.
    Sentry.captureException(err, {
      tags: { kind: 'api_network', method, path },
    })
    throw err
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => null)
    const message = errBody?.message || res.statusText || `HTTP ${res.status} ${method} ${path}`

    // Session expired / not authenticated → clear stale client-side auth so the
    // protected-route guards (RequireAuth/RequireAdmin) re-render and redirect to
    // /login, instead of leaving the app shell + raw error visible. Harmless on
    // public pages and on /login (guards aren't mounted there, so OTP errors show normally).
    if (res.status === 401) {
      useAuthStore.getState().clearAuth()
    }

    // Capture server errors (5xx) to Sentry — these are unexpected and need visibility.
    // 4xx are user errors (validation, auth) and stay out of Sentry to avoid noise.
    if (res.status >= 500) {
      Sentry.captureMessage(`API ${res.status} ${method} ${path}`, {
        level: 'error',
        tags: { kind: 'api_5xx', method, path, status: String(res.status) },
        extra: { responseBody: errBody, message },
      })
    }

    throw Object.assign(new Error(message), { status: res.status, body: errBody })
  }

  const text = await res.text()
  return text ? (JSON.parse(text) as T) : (undefined as unknown as T)
}

export const apiClient = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
}
